import * as THREE from 'three';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { FormaToolbar, FormaToolbarButton } from '../UI/FormaToolbar';
import { Icon } from '../UI/Icon/Icon';
import { WeaveFlyout, WeaveFlyoutElement } from '../UI/WeaveFlyout/WeaveFlyout';
import { WeaveTooltip } from '../UI/WeaveTooltip/WeaveTooltip';
import styles from './styles.module.scss';
import { WindowSettings } from '../WindowSettings/WindowSettings';
import { DailySunlightResult } from '@/types/sunlight';
import { SunlightControls } from '../SunlightControls/SunlightControls';
import { WorkflowProps } from '@/types/workflow';
import { Settings } from '../Settings/Settings';
import { ViewControls } from '../ViewControls/ViewControls';
import { captureScreenshot } from '@/utils/screenshot';
import { AnalysisTab } from '../home/home';
import { ViewFeaturesPanel } from '../ViewFeaturesPanel/ViewFeaturesPanel';
import { PhotonMappingControls } from '../PhotonMappingControls/PhotonMappingControls';
import { useViewFeaturesStore } from '@/store/viewFeaturesStore';
import { useThreeSceneStore } from '@/store/threeSceneStore';
import { WeaveBanner } from '../UI/WeaveBanner/WeaveBanner';

export interface ThreeControlsProps {
	calculateDailySunlightForRooms: (
		selectedFloorIndex?: number | null
	) => Promise<DailySunlightResult[] | undefined>;
	calculateDailySunlightForSelectedGridPoints: () => Promise<DailySunlightResult[] | undefined>;
	onMetricChange: (metric: keyof DailySunlightResult) => void;
	calculatingDailySunlight: boolean;
	clearAllVisualizations: () => void;
	scene: THREE.Scene;
	workflowProps: WorkflowProps;
	previewActive: boolean;
	// Facade sunlight props
	calculateFacadeSunlight: () => Promise<void>;
	clearFacadeVisualization: () => void;
	calculatingFacadeSunlight: boolean;
	facadeSunlightEnabled: boolean;
	startFacadeAnimation: () => Promise<void>;
	stopFacadeAnimation: () => void;
	isAnimating: boolean;
	calculateSingleDay: (dayIndex: number) => Promise<void>;
	// Daily sunlight animation props
	startDailyAnimation: () => Promise<void>;
	stopDailyAnimation: () => void;
	isAnimatingDaily: boolean;
	calculateSingleDayDaily: (dayIndex: number) => Promise<void>;
	currentDate: Date;
	setCurrentDate: (date: Date) => void;
	currentTime: string;
	setCurrentTime: (time: string) => void;
	latitude: number | null;
	setLatitude: (lat: number | null) => void;
	longitude: number | null;
	setLongitude: (lon: number | null) => void;
	sceneRef: MutableRefObject<THREE.Scene | null>;
	rendererRef: MutableRefObject<THREE.WebGLRenderer | null>;
	cameraRef: MutableRefObject<THREE.Camera | null>;
	canvasRef: MutableRefObject<HTMLCanvasElement | null>;
	// Photon mapping props
	currentSunPositionRef: MutableRefObject<THREE.Vector3>;
	onBeforeRunPhotonMapping?: () => Promise<void>;
	onAfterClearPhotons?: () => Promise<void>;
	// Tab state to determine which controls to show
	tab: AnalysisTab;
	selectedBuildingId: string | null;
	availableFloorIndices: number[];
}

export const ThreeControls = (props: ThreeControlsProps) => {
	const PHOTON_MAPPING_ENABLED = import.meta.env.VITE_PHOTON_MAPPING_ENABLED === 'true';

	const { toolbarContainer, optionsIcon } = styles;

	const {
		calculateDailySunlightForRooms,
		calculateDailySunlightForSelectedGridPoints,
		calculatingDailySunlight,
		onMetricChange,
		clearAllVisualizations,
		scene,
		workflowProps,
		previewActive,
		calculateFacadeSunlight,
		clearFacadeVisualization,
		calculatingFacadeSunlight = false,
		facadeSunlightEnabled = false,
		startFacadeAnimation,
		stopFacadeAnimation,
		isAnimating = false,
		isAnimatingDaily = false,
		calculateSingleDay,
		calculateSingleDayDaily,
		startDailyAnimation,
		stopDailyAnimation,
		currentDate,
		setCurrentDate,
		currentTime,
		setCurrentTime,
		latitude,
		setLatitude,
		longitude,
		setLongitude,
		sceneRef,
		rendererRef,
		cameraRef,
		canvasRef,
		currentSunPositionRef,
		onBeforeRunPhotonMapping,
		onAfterClearPhotons,
		tab,
		selectedBuildingId,
		availableFloorIndices,
	} = props;

	const [windowManagementIsOpen, setWindowManagementIsOpen] = useState(false);
	const [sunlightAnalysisIsOpen, setSunlightAnalysisIsOpen] = useState(false);
	const [viewAnalysisIsOpen, setViewAnalysisIsOpen] = useState(false);
	const [terrainConfigurationIsOpen, setTerrainConfigurationIsOpen] = useState(false);
	const [photonMappingIsOpen, setPhotonMappingIsOpen] = useState(false);
	const [settingsIsOpen, setSettingsIsOpen] = useState(false);

	const closeAllFlyouts = () => {
		setWindowManagementIsOpen(false);
		setSunlightAnalysisIsOpen(false);
		setViewAnalysisIsOpen(false);
		setTerrainConfigurationIsOpen(false);
		setPhotonMappingIsOpen(false);
		setSettingsIsOpen(false);
	};

	const handleFlyoutToggle = (flyout: string) => {
		switch (flyout) {
			case 'windowManagement':
				if (!windowManagementIsOpen) closeAllFlyouts();
				if (onAfterClearPhotons) {
					onAfterClearPhotons();
				}
				setWindowManagementIsOpen((prev) => !prev);
				break;
			case 'sunlightAnalysis':
				if (!sunlightAnalysisIsOpen) closeAllFlyouts();
				if (!previewActive) return;
				if (onAfterClearPhotons) {
					onAfterClearPhotons();
				}
				setSunlightAnalysisIsOpen((prev) => !prev);
				break;
			case 'viewAnalysis':
				if (!viewAnalysisIsOpen) closeAllFlyouts();
				if (!previewActive) return;
				if (onAfterClearPhotons) {
					onAfterClearPhotons();
				}
				setViewAnalysisIsOpen((prev) => !prev);
				break;
			case 'terrainConfiguration':
				if (!terrainConfigurationIsOpen) closeAllFlyouts();
				setTerrainConfigurationIsOpen((prev) => !prev);
				break;
			case 'photonMapping':
				if (!photonMappingIsOpen) closeAllFlyouts();
				if (!previewActive) return;
				setPhotonMappingIsOpen((prev) => !prev);
				break;
			case 'settings':
				if (!settingsIsOpen) closeAllFlyouts();
				setSettingsIsOpen((prev) => !prev);
				break;
			default:
				break;
		}
	};

	const onBlur = (e: React.FocusEvent<HTMLDivElement>) => {
		if (e.relatedTarget === null) {
			closeAllFlyouts();
		}
	}

	useEffect(() => {
		if (!previewActive) {
			closeAllFlyouts();
		}
	}, [previewActive]);

	return (
		<div className={toolbarContainer}>
			<FormaToolbar direction="horizontal">
				<ControlMenuItem
					isOpen={windowManagementIsOpen}
					text="Window management"
					onClick={() => handleFlyoutToggle('windowManagement')}
					onBlur={onBlur}
					iconName="forma-offset-24"
					isHidden={tab.id !== 'windows-openings'}
				>
					<WindowSettings />
				</ControlMenuItem>
				<ControlMenuItem
					isOpen={sunlightAnalysisIsOpen}
					text={
						previewActive
							? 'Sunlight analysis'
							: 'Sunlight analysis (select building in 3D view to enable)'
					}
					onClick={() => handleFlyoutToggle('sunlightAnalysis')}
					onBlur={onBlur}
					iconName="forma-analyse-daylight-24"
					disabled={!previewActive}
					isHidden={tab.id !== 'daylight-views'}
				>
					<SunlightControls
						calculateDailySunlightForRooms={(selectedFloorIndex) => {
							handleFlyoutToggle('sunlightAnalysis');
							return calculateDailySunlightForRooms(selectedFloorIndex);
						}}
						calculateDailySunlightForSelectedGridPoints={() => {
							handleFlyoutToggle('sunlightAnalysis');
							return calculateDailySunlightForSelectedGridPoints();
						}}
						availableFloorIndices={availableFloorIndices}
						calculatingDailySunlight={calculatingDailySunlight}
						onMetricChange={onMetricChange}
						clearAllVisualizations={clearAllVisualizations}
						scene={scene}
						workflowProps={workflowProps}
						calculateFacadeSunlight={() => {
							handleFlyoutToggle('sunlightAnalysis');
							return calculateFacadeSunlight();
						}}
						clearFacadeVisualization={clearFacadeVisualization}
						calculatingFacadeSunlight={calculatingFacadeSunlight}
						facadeSunlightEnabled={facadeSunlightEnabled}
						startFacadeAnimation={startFacadeAnimation}
						stopFacadeAnimation={stopFacadeAnimation}
						isAnimating={isAnimating}
						calculateSingleDay={calculateSingleDay}
						startDailyAnimation={startDailyAnimation}
						stopDailyAnimation={stopDailyAnimation}
						isAnimatingDaily={isAnimatingDaily}
						calculateSingleDayDaily={calculateSingleDayDaily}
					/>
				</ControlMenuItem>
				<ControlMenuItem
					isOpen={viewAnalysisIsOpen}
					text={
						previewActive
							? 'View analysis'
							: 'View analysis (select building in 3D view to enable)'
					}
					onClick={() => handleFlyoutToggle('viewAnalysis')}
					onBlur={onBlur}
					iconName="forma-eye-24"
					disabled={!previewActive}
					isHidden={tab.id !== 'daylight-views'}
				>
					<ViewControls
						sceneRef={sceneRef}
						cameraRef={cameraRef}
						selectedBuildingId={selectedBuildingId}
					/>
				</ControlMenuItem>
				<ControlMenuItem
					isOpen={terrainConfigurationIsOpen}
					text="Terrain configuration (mark landmark buildings and paint terrain)"
					onClick={() => handleFlyoutToggle('terrainConfiguration')}
					onBlur={onBlur}
					iconName="forma-fountain-pen-24"
					isHidden={tab.id !== 'windows-openings'}
				>
					<ViewFeaturesPanel sceneRef={sceneRef} />
				</ControlMenuItem>
				<ControlMenuItem
					isOpen={photonMappingIsOpen}
					text={
						previewActive
							? 'Photon mapping'
							: 'Photon mapping (select building in 3D view to enable)'
					}
					onClick={() => handleFlyoutToggle('photonMapping')}
					onBlur={onBlur}
					iconName="forma-analyse-sun-24"
					disabled={!previewActive}
					isHidden={tab.id !== 'daylight-views' || !PHOTON_MAPPING_ENABLED}
				>
					<PhotonMappingControls
						sceneRef={sceneRef}
						currentSunPositionRef={currentSunPositionRef}
						currentDate={currentDate}
						currentTime={currentTime}
						setCurrentTime={setCurrentTime}
						latitude={latitude}
						longitude={longitude}
						onBeforeRunPhotonMapping={onBeforeRunPhotonMapping}
						onAfterClearPhotons={onAfterClearPhotons}
					/>
				</ControlMenuItem>
				<ControlMenuItem
					isOpen={settingsIsOpen}
					text="Settings"
					onClick={() => handleFlyoutToggle('settings')}
					onBlur={onBlur}
					iconName="weave-option"
					iconClassName={optionsIcon}
				>
					<Settings
						currentDate={currentDate}
						setCurrentDate={setCurrentDate}
						currentTime={currentTime}
						setCurrentTime={setCurrentTime}
						latitude={latitude}
						setLatitude={setLatitude}
						longitude={longitude}
						setLongitude={setLongitude}
						sceneRef={sceneRef}
						rendererRef={rendererRef}
					/>
				</ControlMenuItem>
				<ControlMenuItem
					isOpen={false}
					text="Take screenshot"
					onClick={async () => {
						await captureScreenshot({
							canvasRef,
							rendererRef,
							sceneRef,
							cameraRef
						})
					}}
					onBlur={() => { }}
					iconName="forma-camera-24"
				/>
			</FormaToolbar>
			<StatusMessage />
		</div>
	);
};

const ControlMenuItem = ({
	isOpen,
	text,
	onClick,
	onBlur,
	iconName,
	iconClassName = '',
	isHidden = false,
	disabled = false,
	children,
}: {
	isOpen: boolean;
	text: string;
	onClick: () => void;
	onBlur: (e: React.FocusEvent<HTMLDivElement>) => void;
	iconName: string;
	iconClassName?: string;
	isHidden?: boolean;
	disabled?: boolean;
	children?: React.ReactNode;
}) => {
	const flyoutRef = useRef<WeaveFlyoutElement | null>(null);

	useEffect(() => {
		if (!isOpen) return;

		let rafId: number | null = null;
		const refreshPosition = () => {
			if (rafId !== null) return;
			rafId = window.requestAnimationFrame(() => {
				flyoutRef.current?.repositionFlyout?.();
				rafId = null;
			});
		};

		refreshPosition();

		window.addEventListener('scroll', refreshPosition, { capture: true, passive: true });
		window.addEventListener('resize', refreshPosition, { passive: true });

		return () => {
			window.removeEventListener('scroll', refreshPosition, true);
			window.removeEventListener('resize', refreshPosition);
			if (rafId !== null) {
				window.cancelAnimationFrame(rafId);
			}
		};
	}, [isOpen]);

	if (isHidden) return null;

	return (
		<WeaveTooltip
			text={isOpen ? '' : text}
			nub="left-center"
		>
			<WeaveFlyout
				ref={flyoutRef}
				nub="up-left-aligned"
				open={isOpen}
				onBlur={onBlur}>
				<FormaToolbarButton
					slot="trigger"
					active={isOpen}
					onClick={onClick}
					disabled={disabled}
				>
					<Icon name={iconName} className={iconClassName} />
				</FormaToolbarButton>
				<div style={{ padding: '20px' }}>
					{children}
				</div>
			</WeaveFlyout>
		</WeaveTooltip>
	)
}

const StatusMessage = () => {
	const {
		isLandmarkSelectionMode,
		isPaintMode,
		isPaintDeletionMode,
	} = useViewFeaturesStore();
	const {
		isHoleCreationMode,
		isHoleDeletionMode,
		isFacadeViewAnalysisMode,
	} = useThreeSceneStore();

	const [activeMode, setActiveMode] = useState<string | null>(null);

	useEffect(() => {
		if (isHoleCreationMode) {
			setActiveMode('holeCreation');
		} else if (isHoleDeletionMode) {
			setActiveMode('holeDeletion');
		} else if (isLandmarkSelectionMode) {
			setActiveMode('landmarkSelection');
		} else if (isPaintMode) {
			setActiveMode('paint');
		} else if (isPaintDeletionMode) {
			setActiveMode('paintDeletion');
		} else if (isFacadeViewAnalysisMode) {
			setActiveMode('facadeViewAnalysis');
		} else {
			setActiveMode(null);
		}
	}, [isLandmarkSelectionMode, isPaintMode, isPaintDeletionMode, isHoleCreationMode, isHoleDeletionMode, isFacadeViewAnalysisMode]);

	const getStatusMessage = () => {
		switch (activeMode) {
			case 'holeCreation':
				return 'Click on a wall and drag to add a windows.';
			case 'holeDeletion':
				return 'Click on a window to remove it';
			case 'landmarkSelection':
				return 'Click on a building to mark it as a landmark';
			case 'paint':
				return 'Click and drag on the terrain to paint areas';
			case 'paintDeletion':
				return 'Click on painted areas to remove them';
			case 'facadeViewAnalysis':
				return 'Click on building facade to check views from that point';
			default:
				return '';
		}
	};

	if (!getStatusMessage()) {
		return null;
	}

	return (
		<div>
			<WeaveBanner text={getStatusMessage()} variant="info" />
		</div>
	);
};